package com.gloopgo.game.ui

import androidx.compose.foundation.background
import androidx.compose.foundation.border
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.shape.CircleShape
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.clip
import androidx.compose.ui.geometry.Offset
import androidx.compose.ui.graphics.Brush
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.graphics.Shadow
import androidx.compose.ui.text.TextStyle
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.text.style.TextAlign
import androidx.compose.ui.text.style.TextOverflow
import androidx.compose.ui.unit.Dp
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.sp
import com.gloopgo.game.engine.ANIMAL_SKIN_OPTIONS
import gloopgo.shared.generated.resources.Res
import gloopgo.shared.generated.resources.menu_default_player_name
import org.jetbrains.compose.resources.stringResource

/**
 * Platform-resilient avatar blob preview circle.
 *
 * Prevents font metric overflow on iOS (where Apple Color Emoji has tall vertical line metrics)
 * by strictly clipping container bounds and anchoring emoji in the top half and nickname in the bottom half.
 */
@Composable
fun AvatarBlobPreview(
    emoji: String,
    color: Color,
    playerName: String,
    modifier: Modifier = Modifier,
    isCompact: Boolean = false,
    circleSize: Dp = if (isCompact) 48.dp else 58.dp
) {
    val effectiveEmoji = emoji.ifEmpty { ANIMAL_SKIN_OPTIONS.first().emoji }
    val displayName = playerName.trim().ifEmpty {
        stringResource(Res.string.menu_default_player_name)
    }.take(6)

    val outerGlowPadding = if (isCompact) 2.dp else 2.5.dp
    val borderWidth = if (isCompact) 1.5.dp else 2.dp
    val emojiTopPadding = if (isCompact) 3.dp else 5.dp
    val nameBottomPadding = if (isCompact) 4.dp else 5.5.dp
    val nameHorizontalPadding = if (isCompact) 4.dp else 6.dp
    val emojiFontSize = if (isCompact) 17.sp else 21.sp
    val nameFontSize = if (isCompact) 7.sp else 8.5.sp

    Box(
        modifier = modifier
            .size(circleSize)
            .clip(CircleShape)
            .background(color.copy(alpha = 0.25f), CircleShape)
            .padding(outerGlowPadding),
        contentAlignment = Alignment.Center
    ) {
        Box(
            modifier = Modifier
                .fillMaxSize()
                .clip(CircleShape)
                .background(
                    brush = Brush.radialGradient(
                        colors = listOf(color, color.copy(alpha = 0.85f))
                    ),
                    shape = CircleShape
                )
                .border(borderWidth, Color.White, CircleShape)
        ) {
            // Emoji anchored in the upper half
            Text(
                text = effectiveEmoji,
                fontSize = emojiFontSize,
                textAlign = TextAlign.Center,
                modifier = Modifier
                    .align(Alignment.TopCenter)
                    .padding(top = emojiTopPadding)
            )

            // Player nickname anchored in the lower half
            Text(
                text = displayName,
                style = TextStyle(
                    color = Color.White,
                    fontSize = nameFontSize,
                    fontWeight = FontWeight.Bold,
                    textAlign = TextAlign.Center,
                    shadow = Shadow(
                        color = Color.Black.copy(alpha = 0.75f),
                        blurRadius = 2f,
                        offset = Offset(0.5f, 0.5f)
                    )
                ),
                maxLines = 1,
                overflow = TextOverflow.Ellipsis,
                modifier = Modifier
                    .align(Alignment.BottomCenter)
                    .padding(bottom = nameBottomPadding, start = nameHorizontalPadding, end = nameHorizontalPadding)
            )
        }
    }
}
